import { useEffect } from 'react';
import {
  Box,
  Button,
  useColorMode,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginView from './views/LoginView';
import RegisterView from './views/RegisterView';
import HomeView from './views/HomeView';
import DashboardView from './views/DashboardView';
import ForgotPasswordView from './views/ForgotPasswordView';
import ResetPasswordView from './views/ResetPasswordView';

// ScrollToTop component to scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function AppContent() {
  const { colorMode, toggleColorMode } = useColorMode();
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const toast = useToast();

  const { login } = useAuth();
  
  const handleLoginSubmit = ({ email, password, rememberMe }) => {
    // Simulate API call delay
    return new Promise((resolve) => {
      setTimeout(() => {
        // Login the user using the auth context
        login({ email, name: email.split('@')[0] }); // In a real app, you'd get user details from the API
        
        // Show success toast notification
        toast({
          title: 'Login successful',
          description: `Welcome back, ${email}!`,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        resolve();
      }, 1500);
    });
  };
  
  const { register } = useAuth();
  
  const handleRegistrationSubmit = ({ name, email, password }) => {
    // Simulate API call delay
    return new Promise((resolve) => {
      setTimeout(() => {
        // Register the user using the auth context
        register({ name, email });
        
        // Show success toast notification
        toast({
          title: 'Registration successful',
          description: `Welcome, ${name}! Your account has been created.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        resolve();
      }, 1500);
    });
  };
  
  const handleForgotPasswordSubmit = ({ email }) => {
    // Simulate API call delay
    return new Promise((resolve) => {
      setTimeout(() => {
        // In a real application, this would send an email with a reset link
        console.log(`Password reset email sent to ${email}`);
        resolve();
      }, 1500);
    });
  };
  
  const handleResetPasswordSubmit = ({ password, token }) => {
    // Simulate API call delay
    return new Promise((resolve) => {
      setTimeout(() => {
        // Show success toast notification
        toast({
          title: 'Password reset successful',
          description: 'Your password has been updated.',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        console.log(`Password reset with token: ${token}`);
        resolve();
      }, 1500);
    });
  };

  return (
    <Box bg={bgColor} minH="100vh">      
      <Button 
        size="sm" 
        position="fixed" 
        top="4" 
        right="4" 
        zIndex="999"
        onClick={toggleColorMode}
      >
        {colorMode === 'light' ? 'Dark Mode' : 'Light Mode'}
      </Button>
      
      <ScrollToTop />
      
      <Routes>
        <Route path="/" element={<HomeView />} />
        <Route path="/login" element={<LoginView onLoginSubmit={handleLoginSubmit} />} />
        <Route path="/register" element={<RegisterView onRegisterSubmit={handleRegistrationSubmit} />} />
        <Route path="/forgot-password" element={<ForgotPasswordView onForgotPasswordSubmit={handleForgotPasswordSubmit} />} />
        <Route path="/reset-password/:token" element={<ResetPasswordView onResetPasswordSubmit={handleResetPasswordSubmit} />} />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <DashboardView />
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
